import { get } from "lodash";
import {
  Box,
  Flex,
  Icon,
  Loading,
  Select,
  Small,
  TextInput,
} from "orcs-design-system";
import { useEffect, useState, type KeyboardEvent } from "react";
import styled from "styled-components";
import {
  auth0UserMetadataKey,
  DIRECTORY_ROLE,
  POWER_ROLE,
  ADMIN_ROLE,
  DEMAND_LEAD_POWER_SUB_ROLE,
  SUPPLY_LEAD_POWER_SUB_ROLE,
  CUSTOM_POWER_SUB_ROLE,
} from "src/services/auth";
import isValidEmail from "src/util/email";
import { getGraphQLErrors } from "src/util/error";

const IMPERSONATE_ROLE_PLACEHOLDER = (
  <Icon icon={["fas", "user-secret"]} color="iconDefault" />
);

const IMPERSONATION_ROLE_OPTIONS = [
  { value: DIRECTORY_ROLE, label: "Directory" },
  { value: POWER_ROLE, label: "Power" },
  { value: ADMIN_ROLE, label: "Admin" },
];

const POWER_SUB_ROLE_OPTIONS = [
  { value: DEMAND_LEAD_POWER_SUB_ROLE, label: "Demand Lead" },
  { value: SUPPLY_LEAD_POWER_SUB_ROLE, label: "Supply Lead" },
  { value: CUSTOM_POWER_SUB_ROLE, label: "Custom" },
];
const EMPTY_POWER_SUB_ROLES: string[] = [];

const EMAIL_INPUT_COLLAPSED_PX = 48;
const EMAIL_INPUT_EXPANDED_PX = 228;

const FALLBACK_IMPERSONATION_ERROR =
  "Unable to update support impersonation right now. Please try again.";

const getImpersonationErrorMessage = (err: unknown): string =>
  getGraphQLErrors(err)[0]?.message || FALLBACK_IMPERSONATION_ERROR;

type ImpersonationMutationInput = {
  roles?: string[];
  powerSubRoles?: string[];
  email?: string;
};

type SetSupportImpersonation = (args: {
  variables: { input: ImpersonationMutationInput };
}) => Promise<unknown>;

type Auth0LikeUser = Record<string, unknown>;

type SelectOption = { value: string; label: string };

/* Targets ORCS TextInput iconLeft markup; breaks if TextInput wrapper changes — fix opacity in orcs-design-system if needed. */
const ImpersonationEmailShell = styled(Box)<{ $expanded: boolean }>`
  display: inline-block;
  vertical-align: middle;
  max-width: 100%;
  overflow: hidden;
  transition: width 0.18s ease-out;
  width: ${(p) =>
    p.$expanded
      ? `${EMAIL_INPUT_EXPANDED_PX}px`
      : `${EMAIL_INPUT_COLLAPSED_PX}px`};
  /* TextInput iconLeft wrapper sets svg opacity 0.4; match Select placeholder icon */
  & label[for="support-impersonation-email"] svg {
    opacity: 1;
  }
`;

type SupportImpersonationControlsProps = {
  user: Auth0LikeUser;
  setSupportImpersonation: SetSupportImpersonation;
};

const SupportImpersonationControls = ({
  user,
  setSupportImpersonation,
}: SupportImpersonationControlsProps) => {
  const rawImpersonationRoles = get(user, [
    auth0UserMetadataKey,
    "impersonation",
    "roles",
  ]);
  const metadataImpersonationRoles: string[] = Array.isArray(
    rawImpersonationRoles
  )
    ? (rawImpersonationRoles as string[])
    : [];
  const currentImpersonationRole = metadataImpersonationRoles[0] || null;

  const rawImpersonationEmail = get(user, [
    auth0UserMetadataKey,
    "impersonation",
    "email",
  ]);
  const metadataImpersonationEmail =
    typeof rawImpersonationEmail === "string" ? rawImpersonationEmail : "";
  const committedEmail = metadataImpersonationEmail.trim();

  const rawPowerSubRoles = get(user, [
    auth0UserMetadataKey,
    "impersonation",
    "powerSubRoles",
  ]);
  const metadataImpersonationPowerSubRoles: string[] = Array.isArray(
    rawPowerSubRoles
  )
    ? (rawPowerSubRoles as string[])
    : EMPTY_POWER_SUB_ROLES;
  const currentImpersonationPowerSubRoles = metadataImpersonationPowerSubRoles;

  const [impersonationRole, setImpersonationRole] = useState<string | null>(
    currentImpersonationRole
  );
  const [impersonationPowerSubRoles, setImpersonationPowerSubRoles] = useState<
    string[]
  >(currentImpersonationPowerSubRoles);
  const [impersonationEmail, setImpersonationEmail] = useState<string>(
    metadataImpersonationEmail
  );
  const [impersonationError, setImpersonationError] = useState<string>("");
  const [emailValidationError, setEmailValidationError] =
    useState<boolean>(false);
  const [isImpersonationBusy, setIsImpersonationBusy] =
    useState<boolean>(false);
  const [isEmailFieldFocused, setIsEmailFieldFocused] =
    useState<boolean>(false);

  useEffect(() => {
    setImpersonationRole(currentImpersonationRole);
  }, [currentImpersonationRole]);

  useEffect(() => {
    setImpersonationPowerSubRoles(currentImpersonationPowerSubRoles);
  }, [currentImpersonationPowerSubRoles]);

  useEffect(() => {
    setImpersonationEmail(metadataImpersonationEmail);
  }, [metadataImpersonationEmail]);

  const onImpersonationRoleChange = async (option: SelectOption | null) => {
    if (isImpersonationBusy) {
      return;
    }

    const role = option?.value || null;
    const roles = role ? [role] : [];
    const powerSubRoles = role === POWER_ROLE ? impersonationPowerSubRoles : [];
    const previousRole = impersonationRole;
    const previousPowerSubRoles = impersonationPowerSubRoles;

    setImpersonationError("");
    setEmailValidationError(false);
    setImpersonationRole(role);
    setImpersonationPowerSubRoles(powerSubRoles);
    setIsImpersonationBusy(true);

    try {
      await setSupportImpersonation({
        variables: { input: { roles, powerSubRoles } },
      });
      window.location.reload();
    } catch (err) {
      setImpersonationRole(previousRole);
      setImpersonationPowerSubRoles(previousPowerSubRoles);
      setImpersonationError(getImpersonationErrorMessage(err));
      setIsImpersonationBusy(false);
    }
  };

  const onPowerSubRolesChange = async (options: SelectOption[] = []) => {
    if (isImpersonationBusy) {
      return;
    }

    const powerSubRoles = Array.isArray(options)
      ? options.map((option) => option.value)
      : [];
    const previousPowerSubRoles = impersonationPowerSubRoles;

    setImpersonationError("");
    setEmailValidationError(false);
    setImpersonationPowerSubRoles(powerSubRoles);
    setIsImpersonationBusy(true);

    try {
      await setSupportImpersonation({
        variables: { input: { powerSubRoles } },
      });
      window.location.reload();
    } catch (err) {
      setImpersonationPowerSubRoles(previousPowerSubRoles);
      setImpersonationError(getImpersonationErrorMessage(err));
      setIsImpersonationBusy(false);
    }
  };

  // trimmed: callers only pass after validate; must match state used for !== committed check
  const submitEmail = async (trimmed: string) => {
    if (isImpersonationBusy) {
      return;
    }
    if (!isValidEmail(trimmed)) {
      setEmailValidationError(true);
      return;
    }
    setEmailValidationError(false);
    setImpersonationError("");
    setImpersonationEmail(trimmed);
    setIsImpersonationBusy(true);

    try {
      await setSupportImpersonation({
        variables: { input: { email: trimmed } },
      });
      window.location.reload();
    } catch (err) {
      setImpersonationEmail(committedEmail);
      setImpersonationError(getImpersonationErrorMessage(err));
      setIsImpersonationBusy(false);
    }
  };

  const clearCommittedEmail = async () => {
    if (isImpersonationBusy) {
      return;
    }
    setEmailValidationError(false);
    setImpersonationError("");
    setIsImpersonationBusy(true);

    try {
      await setSupportImpersonation({
        variables: { input: { email: "" } },
      });
      window.location.reload();
    } catch (err) {
      setImpersonationEmail(metadataImpersonationEmail);
      setImpersonationError(getImpersonationErrorMessage(err));
      setIsImpersonationBusy(false);
    }
  };

  const onEmailKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") {
      return;
    }
    e.preventDefault();
    const trimmed = (impersonationEmail ?? "").trim();
    if (!trimmed && committedEmail) {
      setEmailValidationError(false);
      void clearCommittedEmail();
      return;
    }
    if (!trimmed) {
      setEmailValidationError(false);
      return;
    }
    if (!isValidEmail(trimmed)) {
      setEmailValidationError(true);
      return;
    }
    setEmailValidationError(false);
    if (trimmed !== committedEmail) {
      void submitEmail(trimmed);
    }
  };

  const selectedOption =
    IMPERSONATION_ROLE_OPTIONS.find((opt) => opt.value === impersonationRole) ||
    null;
  const selectedPowerSubRoleOptions = POWER_SUB_ROLE_OPTIONS.filter((option) =>
    impersonationPowerSubRoles.includes(option.value)
  );

  const emailHasContent = (impersonationEmail ?? "").trim() !== "";
  const emailFieldExpanded =
    isEmailFieldFocused || emailHasContent || emailValidationError;

  return (
    <>
      <Box ml="s">
        <Select
          id="support-impersonation-role"
          hideLabel
          ariaLabel="Impersonate role"
          options={IMPERSONATION_ROLE_OPTIONS}
          value={selectedOption}
          onChange={onImpersonationRoleChange}
          isDisabled={isImpersonationBusy}
          isLoading={isImpersonationBusy}
          isClearable={Boolean(impersonationRole)}
          placeholder={IMPERSONATE_ROLE_PLACEHOLDER}
          height="30px"
          padding="0 8px"
        />
      </Box>
      {impersonationRole === POWER_ROLE && (
        <Box ml="s">
          <Select
            id="support-impersonation-power-sub-roles"
            hideLabel
            ariaLabel="Impersonate power sub-roles"
            options={POWER_SUB_ROLE_OPTIONS}
            value={selectedPowerSubRoleOptions}
            onChange={onPowerSubRolesChange}
            isDisabled={isImpersonationBusy}
            isLoading={isImpersonationBusy}
            isClearable={selectedPowerSubRoleOptions.length > 0}
            isMulti
            placeholder="Power sub-roles"
            height="30px"
            padding="0 8px"
          />
        </Box>
      )}
      <Flex ml="s" alignItems="center">
        <ImpersonationEmailShell $expanded={emailFieldExpanded}>
          <TextInput
            id="support-impersonation-email"
            data-testid="support-impersonation-email"
            type="email"
            placeholder=""
            iconLeft={["fas", "envelope"]}
            fullWidth
            value={impersonationEmail ?? ""}
            onChange={(e) => setImpersonationEmail(e.target.value)}
            onFocus={() => setIsEmailFieldFocused(true)}
            onBlur={() => setIsEmailFieldFocused(false)}
            onKeyDown={onEmailKeyDown}
            disabled={isImpersonationBusy}
            invalid={emailValidationError}
            height="30px"
            ariaLabel="Impersonate email"
          />
        </ImpersonationEmailShell>
        {isImpersonationBusy && <Loading ml="xs" ariaLabel="Saving" />}
      </Flex>
      {impersonationError && (
        <Small ml="s" weight="bold">
          {impersonationError}
        </Small>
      )}
    </>
  );
};

export default SupportImpersonationControls;
